import { Link2Off } from 'lucide-react';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { LinkStatusCard } from '@/components/common/LinkStatusCard';

interface InviteExpiredStateProps {
  /** The invite API's machine error code, if any (drives which pill is active). */
  errorCode?: string;
}

const HEADING_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.INVITE_NOT_FOUND]: UI_STRINGS.INVITE.NOT_FOUND_HEADING,
  [API_ERROR_CODES.INVITE_ALREADY_ACCEPTED]: UI_STRINGS.INVITE.ALREADY_ACCEPTED_HEADING,
};

const BODY_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.INVITE_NOT_FOUND]: UI_STRINGS.INVITE.NOT_FOUND_BODY,
  [API_ERROR_CODES.INVITE_ALREADY_ACCEPTED]: UI_STRINGS.INVITE.ALREADY_ACCEPTED_BODY,
};

// The two status pills only represent the expired/revoked binary (vf-review
// MINOR: showing "Link Expired" highlighted for a not-found or
// already-accepted invite was factually wrong) - not-found and
// already-accepted get their own heading/body above instead, with no pills.
const PILL_CODES = new Set<string>([API_ERROR_CODES.INVITE_EXPIRED, API_ERROR_CODES.INVITE_REVOKED]);

/**
 * Dedicated full-page state for an invite link that can no longer be
 * accepted (expired, revoked, already accepted, or not found) - the Figma
 * "Invite Expired" screen. Distinct from the generic `AccessDeniedState`
 * (used elsewhere for authz failures): this one shows which specific status
 * applies via the two status pills, matching the design.
 */
export function InviteExpiredState({ errorCode }: InviteExpiredStateProps) {
  const isRevoked = errorCode === API_ERROR_CODES.INVITE_REVOKED;
  const showPills = errorCode === undefined || PILL_CODES.has(errorCode);

  return (
    <LinkStatusCard
      icon={<Link2Off size={32} />}
      heading={HEADING_BY_CODE[errorCode ?? ''] ?? UI_STRINGS.INVITE.EXPIRED_HEADING}
      body={BODY_BY_CODE[errorCode ?? ''] ?? UI_STRINGS.INVITE.EXPIRED_BODY}
      pills={
        showPills
          ? [
              { label: UI_STRINGS.INVITE.EXPIRED_PILL_EXPIRED, active: !isRevoked },
              { label: UI_STRINGS.INVITE.EXPIRED_PILL_REVOKED, active: isRevoked },
            ]
          : undefined
      }
      ctaLabel={UI_STRINGS.INVITE.EXPIRED_CTA}
      ctaTo={ROUTES.HOME}
      footnote={UI_STRINGS.INVITE.EXPIRED_FOOTNOTE}
      // Wider than the default 480px so "Invite Already Used" fits on one line.
      maxWidthClassName="max-w-xl"
    />
  );
}
